/**
 * Settings > Import / Export (tracker item 52).
 *
 * Import (ADMIN): choose customers, properties or items, optionally the
 * QuickBooks column names (customers) or a default tax rate (properties),
 * download a template, upload a CSV, check it (dry run: per-row plan and
 * errors, nothing saved), then import. The import is all or nothing and skips
 * rows that already exist. Export (ADMIN, OFFICE_MANAGER): download a dataset
 * as CSV, with a date range where it applies.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IImportExportApi, ITaxRatesApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canExportData, canImportData } from '../../utils/permissions';
import { getErrorCode, getErrorDetails, getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import {
  ACTION_CLASSES,
  ACTION_LABELS,
  EXPORT_OPTIONS,
  PREVIEW_COLUMNS,
  exportRangeError,
  rowCount,
  saveBlob,
} from './importExportUi';

type Api = IImportExportApi & ITaxRatesApi;

function PreviewTable({ entity, rows }: { entity: types.ImportEntity; rows: types.ImportRowResult[] }) {
  const columns = PREVIEW_COLUMNS[entity];
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-md">
      <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="Import preview">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
            <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-700">Result</th>
            {columns.map((c) => (
              <th key={c.key} scope="col" className="px-3 py-2 text-left font-semibold text-gray-700">{c.label}</th>
            ))}
            <th scope="col" className="px-3 py-2 text-left font-semibold text-gray-700">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row) => (
            <tr key={row.rowNumber} data-testid={`import-row-${row.rowNumber}`} className={row.action === 'ERROR' ? 'bg-red-50' : undefined}>
              <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
              <td className="px-3 py-2">
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${ACTION_CLASSES[row.action]}`}>{ACTION_LABELS[row.action]}</span>
              </td>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 text-gray-900 break-words max-w-[14rem]">{row.values[c.key] ?? ''}</td>
              ))}
              <td className="px-3 py-2 text-gray-700">
                {row.errors.length > 0 && (
                  <ul className="list-disc pl-4 text-red-700">
                    {row.errors.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                )}
                {row.skipReason && <span>{row.skipReason}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportSection({ api }: { api: Api }) {
  const [specs, setSpecs] = useState<types.ImportEntitySpec[]>([]);
  const [taxRates, setTaxRates] = useState<types.TaxRate[]>([]);
  const [entity, setEntity] = useState<types.ImportEntity>('customers');
  const [preset, setPreset] = useState<types.ImportPreset>('STANDARD');
  const [defaultTaxRateId, setDefaultTaxRateId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<types.ImportValidation | null>(null);
  const [result, setResult] = useState<types.ImportCommitResult | null>(null);
  const [rejected, setRejected] = useState<types.ImportRowResult[] | null>(null);
  const [busy, setBusy] = useState<'check' | 'import' | 'template' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    api.listImportSpecs().then((s) => { if (live) setSpecs(s); }).catch((cause) => {
      if (live) setError(getErrorMessage(cause, 'Could not load the import column guide'));
    });
    api.listTaxRates().then((t) => { if (live) setTaxRates(t); }).catch(() => { /* properties then need a taxRate column */ });
    return () => { live = false; };
  }, [api]);

  const spec = specs.find((s) => s.entity === entity);
  const options = (): types.ImportUploadOptions => ({
    preset: spec?.presets.includes(preset) ? preset : 'STANDARD',
    ...(entity === 'properties' && defaultTaxRateId ? { defaultTaxRateId } : {}),
  });

  const reset = useCallback(() => {
    setValidation(null);
    setResult(null);
    setRejected(null);
    setError(null);
  }, []);

  function chooseEntity(next: types.ImportEntity) {
    setEntity(next);
    setPreset('STANDARD');
    reset();
  }

  async function downloadTemplate() {
    setBusy('template');
    setError(null);
    try {
      const download = await api.downloadImportTemplate(entity);
      saveBlob(download.blob, download.filename);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not download the template'));
    } finally {
      setBusy(null);
    }
  }

  async function check() {
    if (!file) return;
    setBusy('check');
    reset();
    try {
      setValidation(await api.validateImport(entity, file, options()));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not check the file'));
    } finally {
      setBusy(null);
    }
  }

  async function commit() {
    if (!file || !validation) return;
    setBusy('import');
    setError(null);
    try {
      const done = await api.commitImport(entity, file, { ...options(), expectedSha256: validation.fileSha256 });
      setResult(done);
      setValidation(null);
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
    } catch (cause) {
      setError(getErrorMessage(cause, 'The import failed; nothing was saved'));
      if (getErrorCode(cause) === 'IMPORT_HAS_ERRORS') {
        const rows = getErrorDetails(cause)?.rows;
        setRejected(Array.isArray(rows) ? (rows as types.ImportRowResult[]) : null);
        setValidation(null);
      }
    } finally {
      setBusy(null);
    }
  }

  const summary = validation?.summary;
  const canCommit = !!validation && summary!.error === 0 && summary!.create > 0 && busy === null;

  return (
    <section className="bg-white rounded-lg shadow p-5 space-y-4" aria-labelledby="import-heading">
      <div>
        <h2 id="import-heading" className="text-lg font-semibold text-gray-900">Import from CSV</h2>
        <p className="mt-1 text-sm text-gray-600">
          Load customers first, then their properties (service addresses), and your items. Check the file first: nothing is saved
          until you import, the import is all or nothing, and rows that already exist are skipped, so importing the same file twice
          is safe. Invoices, payments and opening balances are not imported.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="import-entity" className={labelClass}>What to import</label>
          <select id="import-entity" className={inputClass} value={entity} onChange={(e) => chooseEntity(e.target.value as types.ImportEntity)}>
            <option value="customers">Customers</option>
            <option value="properties">Properties (service addresses)</option>
            <option value="items">Items (price list)</option>
          </select>
        </div>
        {spec?.presets.includes('QUICKBOOKS') && (
          <div>
            <label htmlFor="import-preset" className={labelClass}>Column names</label>
            <select id="import-preset" className={inputClass} value={preset} onChange={(e) => { setPreset(e.target.value as types.ImportPreset); reset(); }}>
              <option value="STANDARD">This app&apos;s template</option>
              <option value="QUICKBOOKS">QuickBooks customer list export</option>
            </select>
          </div>
        )}
        {entity === 'properties' && (
          <div>
            <label htmlFor="import-tax-rate" className={labelClass}>Default tax rate</label>
            <select id="import-tax-rate" className={inputClass} value={defaultTaxRateId} onChange={(e) => { setDefaultTaxRateId(e.target.value); reset(); }}>
              <option value="">None: every row needs a taxRate</option>
              {taxRates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {spec && (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-blue-700">Columns for {spec.label.toLowerCase()}</summary>
          <p className="mt-2 text-gray-600">{spec.description}</p>
          <ul className="mt-2 space-y-1">
            {spec.columns.map((c) => (
              <li key={c.name}>
                <code className="font-mono text-gray-900">{c.name}</code>
                {c.required && <span className="ml-1 text-xs font-semibold text-red-700">required</span>}
                <span className="text-gray-600"> {c.description}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="import-file" className={labelClass}>CSV file (up to 10 MB, 5,000 rows)</label>
          <input
            id="import-file"
            ref={fileInput}
            type="file"
            accept=".csv,.txt,text/csv"
            className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-700"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); reset(); }}
          />
        </div>
        <button type="button" className={secondaryButtonClass} onClick={() => void downloadTemplate()} disabled={busy !== null}>
          {busy === 'template' ? 'Downloading…' : 'Download template'}
        </button>
        <button type="button" className={primaryButtonClass} onClick={() => void check()} disabled={!file || busy !== null}>
          {busy === 'check' ? 'Checking…' : 'Check file'}
        </button>
      </div>

      {validation && summary && (
        <div className="space-y-3" aria-label="Check result">
          <p className="text-sm text-gray-900" data-testid="import-summary">
            <strong>{validation.filename}</strong>: {rowCount(summary.totalRows)}. {summary.create} new, {summary.skip} already exist, {summary.error} with errors.
          </p>
          {validation.warnings.map((w) => <p key={w} className="text-sm text-amber-800 bg-amber-50 rounded px-3 py-2">{w}</p>)}
          {validation.newCategories.length > 0 && (
            <p className="text-sm text-gray-700">New categories: {validation.newCategories.join(', ')}</p>
          )}
          {summary.error > 0 && (
            <p className="text-sm text-red-700" role="note">Fix the rows marked Error in your file and check it again. Nothing can be imported while any row has an error.</p>
          )}
          <PreviewTable entity={validation.entity} rows={validation.rows} />
          {validation.rowsTruncated && (
            <p className="text-xs text-gray-500">Showing every row with an error and the first 100 others.</p>
          )}
          <div className="flex flex-wrap gap-3">
            <button type="button" className={primaryButtonClass} onClick={() => void commit()} disabled={!canCommit}>
              {busy === 'import' ? 'Importing…' : `Import ${rowCount(summary.create)}`}
            </button>
            <button type="button" className={secondaryButtonClass} onClick={reset} disabled={busy !== null}>Cancel</button>
          </div>
        </div>
      )}

      {rejected && rejected.length > 0 && <PreviewTable entity={entity} rows={rejected} />}

      {result && (
        <div className="rounded-md bg-green-50 px-4 py-3 text-sm text-green-900" role="status" data-testid="import-result">
          <p className="font-semibold">Import complete: {result.created} created, {result.skipped} skipped{result.categoriesCreated > 0 ? `, ${result.categoriesCreated} new categories` : ''}.</p>
          {result.skippedRows.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {result.skippedRows.slice(0, 20).map((r) => <li key={r.rowNumber}>Row {r.rowNumber}: {r.reason}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ExportSection({ api }: { api: Api }) {
  const [dataset, setDataset] = useState<types.ExportDataset>('customers');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const option = EXPORT_OPTIONS.find((o) => o.dataset === dataset)!;
  const rangeError = option.dateField ? exportRangeError(from, to) : null;

  async function download() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const file = await api.downloadExport(dataset, option.dateField ? { from: from || undefined, to: to || undefined } : {});
      saveBlob(file.blob, file.filename);
      setNotice(`Downloaded ${file.filename}`);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not export the data'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white rounded-lg shadow p-5 space-y-4" aria-labelledby="export-heading">
      <div>
        <h2 id="export-heading" className="text-lg font-semibold text-gray-900">Export to CSV</h2>
        <p className="mt-1 text-sm text-gray-600">
          Download your data to open in a spreadsheet or bring into QuickBooks Online (Sales &gt; Invoices &gt; Import invoices). Text that
          starts like a spreadsheet formula is prefixed with an apostrophe.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-3">
          <label htmlFor="export-dataset" className={labelClass}>Data</label>
          <select id="export-dataset" className={inputClass} value={dataset} onChange={(e) => setDataset(e.target.value as types.ExportDataset)}>
            {EXPORT_OPTIONS.map((o) => <option key={o.dataset} value={o.dataset}>{o.label}</option>)}
          </select>
          <p className="mt-1 text-sm text-gray-500">{option.description}</p>
        </div>
        {option.dateField && (
          <>
            <div>
              <label htmlFor="export-from" className={labelClass}>From ({option.dateField})</label>
              <input id="export-from" type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label htmlFor="export-to" className={labelClass}>To</label>
              <input id="export-to" type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </>
        )}
      </div>
      {rangeError && <p className="text-sm text-red-700">{rangeError}</p>}
      <button type="button" className={primaryButtonClass} onClick={() => void download()} disabled={busy || !!rangeError}>
        {busy ? 'Preparing…' : 'Download CSV'}
      </button>
    </section>
  );
}

export function ImportExportPage() {
  const api = useMemo<Api>(() => createAPIClient(), []);
  const { user } = useAuth();
  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Import / Export</h1>
      </div>
      {canImportData(user) && <ImportSection api={api} />}
      {canExportData(user) && <ExportSection api={api} />}
    </main>
  );
}
